import { TitleButton } from '@/common/buttons/TitleButton';
import { useDialog } from '@/common/dialog/DialogContext';
import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { useEntityMgr } from '@/common/entityFramework/entityView/EntityMgrContext';
import { GridConstants } from '@/common/grid/GridConstants';
import { useNavState } from '@/common/navState/NavStateContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { ObjUtils } from '@/common/utils/ObjUtils';
import { CalcJobDialog } from '@/gp/entities/calc/calcJobDialog/CalcJobDialog';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useCallback, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

/**
 * Per requirements GP-9, this provides a list of calculations that were processed, filtered by a type declared in the URL (price, reallocation, or estimate)
 **/
export const CalcList = () => {
  const { openDialog } = useDialog();
  const { entityObj, setExportReq } = useEntityMgr();
  const { calcEntity } = useParams();
  const { navigateTo } = useNavState();
  const [isCompareDisabled, setIsCompareDisabled] = useState(true);
  const entityListRef = useRef();

  const openCalcJobDialog = () => {
    const calcEntityConst = calcEntity ? `${calcEntity.toUpperCase()}_CALC` : null;

    const data = {
      entity: calcEntityConst,
      entityId: entityObj.id,
    };
    openDialog(CalcJobDialog, { data });
  };

  const augRowsReq = useCallback((rowsReq) => {
    if (!calcEntity) {
      return;
    }

    let typeFilterList;
    if (calcEntity.toUpperCase() === 'PRICE') {
      typeFilterList = ['AGG_PRICE', 'DERIVED_PRICE', 'MIN_PRICE'];
    } else if (calcEntity.toUpperCase() === 'REALLOC') {
      typeFilterList = [
        'REALLOC_MON',
        'REALLOC_QTR',
        'REALLOC_MULTI_PER',
        'REALLOC_MULTI_PER_UNQ',
        'SOR_REALLOC',
        'REALLOC_SPLIT',
      ];
    } else if (calcEntity.toUpperCase() === 'EST') {
      typeFilterList = ['EST_BPI', 'EST_BPA'];
    } else {
      return;
    }

    rowsReq.expr = ExprUtils.and(rowsReq.expr, ExprUtils.whereParams('type', typeFilterList));
  }, []);

  const navigateToCalcCompare = () => {
    const gridApi = entityListRef.current?.gridRef.current?.gridApiRef.current;
    const selectedRows = gridApi?.getSelectedRows?.() ?? [];
    if (selectedRows.length !== 2) {
      return;
    }
    const calcIds = selectedRows.map((row) => row.id ?? row.rowId).filter(Boolean);
    navigateTo('gp/compare/calc-prod', { calcIds });
  };

  const augButtons = (buttons) => {
    ObjUtils.clearObj(buttons);
    buttons.push(
      <TitleButton
        key="compareCalcButton"
        icon={fas.faBalanceScale}
        disabled={isCompareDisabled}
        toolTip="Compare two Calculations with the same Policy Type"
        onClick={navigateToCalcCompare}
      />,
      <TitleButton
        key="runCalcButton"
        icon={fas.faPlus}
        toolTip="Run New Calculation"
        dataTestId="add-button"
        onClick={openCalcJobDialog}
      />,
    );
  };

  const augFieldDefs = (fieldToFieldDef) => {
    fieldToFieldDef.checkbox = GridConstants.checkbox;
  };

  const policyCodeForRow = (row) => row?.polType?.code ?? row?.polType ?? row?.pol?.code ?? row?.polCode;

  const handleSelectionChanged = (event) => {
    const selectedRows = event.api.getSelectedRows();

    const selectedCalcIds = selectedRows.map((row) => row.id ?? row.rowId).filter(Boolean);
    setExportReq((prev) => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        entityIds: selectedCalcIds,
        entityId: selectedCalcIds[0] ?? prev.entityId,
      };
    });

    if (selectedRows.length !== 2) {
      setIsCompareDisabled(true);
      return;
    }

    const [first, second] = selectedRows;
    const firstCode = policyCodeForRow(first);
    const secondCode = policyCodeForRow(second);
    setIsCompareDisabled(!(firstCode && firstCode === secondCode));
  };

  const getContextMenuItems = useCallback(
    (params) => {
      const multipleSelected = params.api.getSelectedRows().length > 1;
      let contextMenuItems = entityListRef.current.getContextMenuItems(params);

      if (multipleSelected) {
        contextMenuItems = contextMenuItems.map((item) => ({ ...item, disabled: true }));
      }

      return contextMenuItems;
    },
    [entityListRef],
  );

  return (
    <EntityList
      ref={entityListRef}
      entity="CALC"
      augFieldDefs={augFieldDefs}
      augRowsReq={augRowsReq}
      augButtons={augButtons}
      gridOptions={{
        rowSelection: 'multiple',
        getContextMenuItems: getContextMenuItems,
      }}
      events={{
        onSelectionChanged: handleSelectionChanged,
      }}
    />
  );
};
